import { getAccessToken, getUser, isSupabaseAuthConfigured } from './supabaseAuth';
import { isSupabaseConfigured } from './supabaseContent';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

async function apiGetAnon(path) {
  const response = await fetch(`${SUPABASE_URL}${path}`, {
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Supabase request failed: ${response.status} ${message}`);
  }
  return response.json();
}

async function apiGetAuth(path) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${SUPABASE_URL}${path}`, {
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Supabase request failed: ${response.status} ${message}`);
  }
  return response.json();
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export async function fetchLessonStepCounts(lessonDbIds) {
  if (!isSupabaseConfigured()) return new Map();
  const ids = (lessonDbIds || []).filter(Boolean);
  if (!ids.length) return new Map();

  const result = new Map();

  // Prefer a view `lesson_step_counts` if present (recommended migration).
  // Fallback: count client-side by fetching lesson_steps rows (MVP-safe for small datasets).
  for (const group of chunk(ids, 50)) {
    const inList = group.map((id) => `"${id}"`).join(',');
    try {
      const rows = await apiGetAnon(
        `/rest/v1/lesson_step_counts?lesson_id=in.(${inList})&select=lesson_id,steps_count`
      );
      for (const r of rows) {
        result.set(r.lesson_id, Number(r.steps_count || 0));
      }
      continue;
    } catch {
      // ignore and fall back for this chunk
    }

    const steps = await apiGetAnon(
      `/rest/v1/lesson_steps?lesson_id=in.(${inList})&select=lesson_id`
    );
    const counts = new Map();
    for (const s of steps) {
      counts.set(s.lesson_id, (counts.get(s.lesson_id) || 0) + 1);
    }
    for (const [k, v] of counts.entries()) result.set(k, v);
  }

  return result;
}

export async function fetchUserLessonProgress(lessonDbIds) {
  if (!isSupabaseAuthConfigured()) return new Map();
  const user = getUser();
  if (!user?.id) return new Map();
  const ids = (lessonDbIds || []).filter(Boolean);
  if (!ids.length) return new Map();

  const result = new Map();

  for (const group of chunk(ids, 50)) {
    const inList = group.map((id) => `"${id}"`).join(',');
    const rows = await apiGetAuth(
      `/rest/v1/user_lesson_progress?lesson_id=in.(${inList})&select=lesson_id,current_step,completed,completed_at,updated_at`
    );
    for (const r of rows) {
      result.set(r.lesson_id, {
        currentStep: Number(r.current_step || 0),
        completed: Boolean(r.completed),
        completedAt: r.completed_at || null,
        updatedAt: r.updated_at || null
      });
    }
  }

  return result;
}

